/**
 * Die Blätter des Liefervertrags im Bereich *Energie* (Spec 46c, „Web —
 * Besonderheiten": „Die Blätter öffnet `Energie.tsx`"). Dieser Baustein sitzt
 * in `routes/`, weil er zwei Features zusammenführt: das Blatt gehört
 * `features/energy`, der **Anbieter** ist ein Absender aus `features/documents`
 * (`SenderField`, `SenderSheet`) — Features kennen einander nicht.
 *
 * Er besitzt die Anbieterauswahl und das Blatt „Neuer Anbieter". Dieses steht
 * als Geschwister der Vertragsblätter, nicht darin: zwei unabhängige
 * Radix-`Dialog`s stapeln sich sauber, ein Formular im Portal des anderen
 * nicht (dasselbe wie `DocumentSheet`).
 */

import { useEffect, useRef, useState } from 'react'

import type { FieldError } from '@/components/FormField'
import { SenderField } from '@/features/documents/SenderField'
import { SenderSheet } from '@/features/documents/SenderSheet'
import type { MeterDetail, SupplyContract } from '@/features/energy/api'
import { SupplyCreateSheet, SupplyEditSheet } from '@/features/energy/SupplyContractSheet'
import type { SupplyMeterRef, SupplyProvider } from '@/features/energy/SupplyContractSheet'

export type SupplySheetState =
  | { readonly mode: 'create' }
  | { readonly mode: 'edit'; readonly contract: SupplyContract }

interface SupplyContractSheetsProps {
  readonly meter: MeterDetail
  /** `null`: beide Blätter sind zu. */
  readonly state: SupplySheetState | null
  readonly onClose: () => void
}

function initialProvider(state: SupplySheetState | null): SupplyProvider | null {
  if (state?.mode !== 'edit' || state.contract.provider === null) return null
  return { id: state.contract.provider.id, label: state.contract.provider.label }
}

export function SupplyContractSheets({ meter, state, onClose }: SupplyContractSheetsProps) {
  const [provider, setProvider] = useState<SupplyProvider | null>(null)
  const [newProvider, setNewProvider] = useState<{ readonly searchText: string } | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)

  // Jedes Öffnen beginnt mit der Auswahl des Vertrags (Bearbeiten) oder leer
  // (Anlegen) — während des Renderns verglichen, nicht in einem Effekt.
  const openKey = state === null ? null : state.mode === 'create' ? 'create' : state.contract.id
  const [syncedKey, setSyncedKey] = useState(openKey)
  if (openKey !== syncedKey) {
    setSyncedKey(openKey)
    setProvider(initialProvider(state))
  }

  // Das Bearbeiten-Blatt gibt den Fokus an den Auslöser zurück (Zeile oder Karte).
  useEffect(() => {
    triggerRef.current =
      openKey === null || openKey === 'create'
        ? null
        : document.querySelector<HTMLButtonElement>(`[data-testid="meter-supply-open-${openKey}"], [data-testid="supply-edit"]`)
  }, [openKey])

  function providerField(prefix: 'supply-create' | 'supply-edit') {
    return (error: FieldError | undefined) => (
      <SenderField
        value={provider}
        legacyText={null}
        onSelect={(selection) => {
          if (selection !== null) setProvider(selection)
        }}
        onRequestNew={(searchText) => {
          setNewProvider({ searchText })
        }}
        error={error}
        testIdPrefix={`${prefix}-provider`}
        noun="Anbieter"
        apiField="provider_id"
        errorPrefix={prefix}
        allowNone={false}
      />
    )
  }

  const meterRef: SupplyMeterRef = {
    id: meter.id,
    label: meter.label,
    houseLabel: meter.house?.label ?? null,
  }

  return (
    <>
      <SupplyCreateSheet
        open={state?.mode === 'create'}
        onOpenChange={(open) => {
          if (!open) onClose()
        }}
        meter={meterRef}
        provider={provider}
        providerField={providerField('supply-create')}
        onCreated={onClose}
      />
      <SupplyEditSheet
        open={state?.mode === 'edit'}
        onOpenChange={(open) => {
          if (!open) onClose()
        }}
        contract={state?.mode === 'edit' ? state.contract : null}
        provider={provider}
        providerField={providerField('supply-edit')}
        triggerRef={triggerRef}
        onSaved={onClose}
        onDeleted={onClose}
      />
      <SenderSheet
        open={newProvider !== null}
        onOpenChange={(open) => {
          if (!open) setNewProvider(null)
        }}
        sender={null}
        initialName={newProvider?.searchText ?? ''}
        onCreated={(created) => {
          setProvider({ id: created.id, label: created.name })
        }}
      />
    </>
  )
}
